import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  Box,
  Card,
  CardActionArea,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { RelatedEntityItem } from "../../types";

interface ImageCarouselProps {
  mediaItems: RelatedEntityItem[];
  onSelectImage?: (item: RelatedEntityItem) => void;
  selectedItemId: number | null;
  disablePreview?: boolean;
}

// Helper to format relationships for display
const formatRelationships = (relationships: any) => {
  if (!relationships) return "No relationships";
  return Object.entries(relationships)
    .map(([type, items]) => {
      const itemsList = Array.isArray(items)
        ? items.map((item) => `${item?.id}: ${item?.name}`).join(", ")
        : "No items";
      return `${type}: [${itemsList}]`;
    })
    .join("\n");
};

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  mediaItems,
  onSelectImage,
  selectedItemId,
  disablePreview,
}) => {
  const [previewItem, setPreviewItem] = useState<RelatedEntityItem | null>(
    null
  );
  
  if (mediaItems.length === 0) {
    return null;
  }

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4,
      slidesToSlide: 4, // Skip entire page (4 items)
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 3,
      slidesToSlide: 3, // Skip entire page (3 items)
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 2,
      slidesToSlide: 2, // Skip entire page (2 items)
    },
  };

  const handleCardClick = (item: RelatedEntityItem) => {
    console.log('[ImageCarousel] Card clicked', {
      item,
      selectedItemId,
      willCallOnSelect: !!onSelectImage
    });
    if (onSelectImage) {
      onSelectImage(item);
    }
  };

  // Custom arrow components to prevent prop warnings
  const CustomLeftArrow = ({ onClick }: { onClick?: () => void }) => (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        left: 0,
        zIndex: 2,
        color: "primary.main",
        bgcolor: "rgba(255, 255, 255, 0.8)",
        "&:hover": {
          bgcolor: "rgba(255, 255, 255, 1)",
        },
      }}
    >
      <ArrowCircleLeftIcon />
    </IconButton>
  );

  const CustomRightArrow = ({ onClick }: { onClick?: () => void }) => (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        right: 0,
        zIndex: 2,
        color: "primary.main",
        bgcolor: "rgba(255, 255, 255, 0.8)",
        "&:hover": {
          bgcolor: "rgba(255, 255, 255, 1)",
        },
      }}
    >
      <ArrowCircleRightIcon />
    </IconButton>
  );

  return (
    <Box 
      sx={{ 
        width: "100%", 
        mt: 2,
        minHeight: "170px", // Reduced from 200px - just enough for cards + margins
      }}
    >
      <Carousel
        responsive={responsive}
        infinite={false} // Disable infinite scroll 
        customLeftArrow={<CustomLeftArrow />}
        customRightArrow={<CustomRightArrow />}
        removeArrowOnDeviceType={["mobile"]}
        ssr={false}
        partialVisible={false}
      >
        {mediaItems.map((item, index) => (
                      <Card
              key={item.id}
              sx={{
                m: 1,
                position: "relative",
                border: item.id === selectedItemId ? "2px solid" : "none",
                borderColor: "primary.main",
                transition: "transform 0.15s ease-out", // Faster, smoother transition
                minHeight: "160px", // Reduced from 180px - just enough for 150px image + text + margins
                minWidth: "120px", // Keep this - ensures cards have proper width
                "&:hover": {
                  transform: "scale(1.02)", // Reduced from 1.05 to minimize layout shifts
                },
                // Prevent layout thrashing
                willChange: "transform", // Hint browser for GPU acceleration
                backfaceVisibility: "hidden", // Prevent flicker
              }}
            >
            <CardActionArea onClick={() => handleCardClick(item)}>
              {item.entity_thumbnail ? (
                                  <Box
                    component="img"
                    src={item.entity_thumbnail}
                    alt={item.entity_name || item.name || ""}
                    sx={{
                      width: "100%",
                      height: 150,
                      objectFit: "cover",
                      display: "block", // Prevent extra spacing
                    }}
                    loading="lazy" // Improve performance for images
                  />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: 150,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.100",
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 40, color: "grey.500" }} />
                </Box>
              )}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  p: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "white",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.entity_name || item.name || ""}
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Carousel>
    </Box>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ImageCarousel, (prevProps, nextProps) => {
  // Only re-render if these key props actually change
  return (
    prevProps.mediaItems.length === nextProps.mediaItems.length &&
    prevProps.selectedItemId === nextProps.selectedItemId &&
    prevProps.onSelectImage === nextProps.onSelectImage &&
    prevProps.mediaItems.every((item, index) => 
      item.id === nextProps.mediaItems[index]?.id
    )
  );
});
